import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Calendar, CreditCard, Search, RefreshCw, Loader2, 
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Clock,
  Pause, Play, X, Plus, Eye, Settings, DollarSign, RotateCcw,
  CalendarClock, ExternalLink
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

// Status badge colors
const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

const statusLabels = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Check if plan is overdue (active and past next_payment_date)
const isOverdue = (plan) => {
  if (plan.status !== 'active' || !plan.next_payment_date) return false;
  return new Date(plan.next_payment_date) < new Date();
};

// Check if due soon (within 7 days)
const isDueSoon = (plan) => {
  if (plan.status !== 'active' || !plan.next_payment_date) return false;
  const nextDate = new Date(plan.next_payment_date);
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return nextDate > now && nextDate <= sevenDaysFromNow;
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'text-slate-900',
    success: 'text-emerald-600',
    warning: 'text-orange-600',
    danger: 'text-red-600',
    muted: 'text-slate-500',
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-1">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{title}</span>
      </div>
      <p className={cn("text-xl font-bold", variants[variant])}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
};

// Section component for drawer
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-slate-50 rounded">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
          {title}
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function WeekenderPaymentPlans({ 
  event, 
  bookings,
  loading: bookingsLoading, 
  authToken,
  onRefresh,
  onBookingSelect
}) {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('next_payment');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Drawer state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Action states
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, plan: null });
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch all payment plans
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllPlans(data.plans || []);
      } else {
        console.error('Failed to fetch payment plans');
      }
    } catch (error) {
      console.error('Error fetching payment plans:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Filter plans for this event by matching booking_id with event bookings
  const eventBookingIds = useMemo(() => {
    return new Set((bookings || []).map(b => b.booking_id));
  }, [bookings]);

  const eventPlans = useMemo(() => {
    return allPlans.filter(plan => eventBookingIds.has(plan.booking_id));
  }, [allPlans, eventBookingIds]);

  // Summary stats for this event only
  const stats = useMemo(() => {
    const activePlans = eventPlans.filter(p => p.status === 'active');
    const pausedPlans = eventPlans.filter(p => p.status === 'paused');
    const overduePlans = activePlans.filter(p => isOverdue(p));
    const dueSoonPlans = activePlans.filter(p => isDueSoon(p));
    
    const totalRemaining = activePlans.reduce((sum, p) => {
      const remaining = Math.max(0, (p.booking_total || 0) - (p.booking_amount_paid || 0));
      return sum + remaining;
    }, 0);
    
    return {
      total: eventPlans.length,
      active: activePlans.length,
      paused: pausedPlans.length,
      overdue: overduePlans.length,
      dueSoon: dueSoonPlans.length,
      completed: eventPlans.filter(p => p.status === 'completed').length,
      totalRemaining
    };
  }, [eventPlans]);

  // Filter and sort
  const filteredPlans = useMemo(() => {
    let filtered = [...eventPlans];
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.customer_name?.toLowerCase().includes(query) ||
        p.customer_email?.toLowerCase().includes(query) ||
        p.booking_id?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(p => p.status === 'active');
    } else if (statusFilter === 'paused') {
      filtered = filtered.filter(p => p.status === 'paused');
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(p => p.status === 'completed');
    } else if (statusFilter === 'cancelled') {
      filtered = filtered.filter(p => p.status === 'cancelled');
    } else if (statusFilter === 'overdue') {
      filtered = filtered.filter(p => isOverdue(p));
    } else if (statusFilter === 'due_soon') {
      filtered = filtered.filter(p => isDueSoon(p));
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'customer':
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
          break;
        case 'instalment':
          aVal = a.monthly_amount || 0;
          bVal = b.monthly_amount || 0;
          break;
        case 'paid':
          aVal = a.booking_amount_paid || 0;
          bVal = b.booking_amount_paid || 0;
          break;
        case 'remaining':
          aVal = Math.max(0, (a.booking_total || 0) - (a.booking_amount_paid || 0));
          bVal = Math.max(0, (b.booking_total || 0) - (b.booking_amount_paid || 0));
          break;
        case 'next_payment':
          aVal = a.next_payment_date ? new Date(a.next_payment_date).getTime() : Infinity;
          bVal = b.next_payment_date ? new Date(b.next_payment_date).getTime() : Infinity;
          break;
        case 'status':
          // Overdue first, then active, paused, completed, cancelled
          const statusOrder = { active: isOverdue(a) ? 0 : 1, paused: 2, completed: 3, cancelled: 4 };
          aVal = statusOrder[a.status] ?? 5;
          bVal = statusOrder[b.status] ?? 5;
          break;
        default:
          aVal = a.plan_id;
          bVal = b.plan_id;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [eventPlans, searchQuery, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // Open plan drawer
  const openPlanDrawer = (plan) => {
    setSelectedPlan(plan);
    setDrawerOpen(true);
  };

  // Close drawer
  const closePlanDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedPlan(null), 300);
  };

  // Action handlers (reusing existing endpoints)
  const handleProcessPayment = async (plan) => {
    setProcessingPlanId(plan.plan_id);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/process`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success('Payment processed successfully');
        fetchPlans();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process payment');
      }
    } catch (error) {
      toast.error('Failed to process payment');
    } finally {
      setProcessingPlanId(null);
      setActionDialog({ open: false, type: null, plan: null });
    }
  };

  const handlePausePlan = async (plan) => {
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/pause`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('Payment plan paused');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to pause plan');
      }
    } catch (error) {
      toast.error('Failed to pause plan');
    }
  };

  const handleResumePlan = async (plan) => {
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/resume`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('Payment plan resumed');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resume plan');
      }
    } catch (error) {
      toast.error('Failed to resume plan');
    }
  };

  const handleCancelPlan = async (plan) => {
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/cancel`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('Payment plan cancelled');
        fetchPlans();
        setActionDialog({ open: false, type: null, plan: null });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel plan');
      }
    } catch (error) {
      toast.error('Failed to cancel plan');
    }
  };

  const handleRecalculateFromBooking = async (plan) => {
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/recalculate-from-booking`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('Plan recalculated from booking');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to recalculate');
      }
    } catch (error) {
      toast.error('Failed to recalculate');
    }
  };

  // View linked booking
  const handleViewBooking = (plan) => {
    const booking = bookings?.find(b => b.booking_id === plan.booking_id);
    if (booking && onBookingSelect) {
      closePlanDrawer();
      onBookingSelect(booking);
    }
  };

  const isLoading = loading || bookingsLoading;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Payment Plans</h2>
          <p className="text-sm text-slate-500">Active instalment plans for this event</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchPlans(); onRefresh?.(); }}
            disabled={isLoading}
            className="text-slate-500 hover:text-slate-700"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            asChild
            className="text-slate-600"
          >
            <a href="/admin" onClick={(e) => {
              e.preventDefault();
              // Navigate to global payment plans - would need router integration
              toast.info('Navigate to Weekenders → Payment Plans for all events');
            }}>
              <ExternalLink className="w-4 h-4 mr-1.5" />
              View All Plans
            </a>
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard
          title="Active Plans"
          value={stats.active}
          subtitle={stats.total > 0 ? `of ${stats.total} total` : 'No plans'}
          icon={CalendarClock}
          variant={stats.active > 0 ? "success" : "muted"}
        />
        <StatCard
          title="Amount Remaining"
          value={formatCurrency(stats.totalRemaining)}
          icon={DollarSign}
        />
        <StatCard
          title="Due Soon"
          value={stats.dueSoon}
          subtitle="Within 7 days"
          icon={Clock}
          variant={stats.dueSoon > 0 ? "warning" : "muted"}
        />
        <StatCard
          title="Overdue"
          value={stats.overdue}
          subtitle="Needs attention"
          icon={AlertTriangle}
          variant={stats.overdue > 0 ? "danger" : "muted"}
        />
        <StatCard
          title="Paused"
          value={stats.paused}
          icon={Pause}
          variant={stats.paused > 0 ? "warning" : "muted"}
        />
      </div>

      {/* Plans Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-violet-600" />
            Payment Plans
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, booking ref..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="due_soon">Due Soon</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="text-sm text-slate-500 mb-3">
            {filteredPlans.length} of {eventPlans.length} plans
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : eventPlans.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CalendarClock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No payment plans for this event</p>
              <p className="text-sm mt-1">Plans are created when customers set up instalments during booking</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('customer')}
                      >
                        <span className="flex items-center">CUSTOMER <SortIcon field="customer" /></span>
                      </th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('status')}
                      >
                        <span className="flex items-center">STATUS <SortIcon field="status" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('instalment')}
                      >
                        <span className="flex items-center justify-end">INSTALMENT <SortIcon field="instalment" /></span>
                      </th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('next_payment')}
                      >
                        <span className="flex items-center">NEXT PAYMENT <SortIcon field="next_payment" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('paid')}
                      >
                        <span className="flex items-center justify-end">PAID <SortIcon field="paid" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('remaining')}
                      >
                        <span className="flex items-center justify-end">REMAINING <SortIcon field="remaining" /></span>
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">CARD</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlans.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-500">
                          No plans match your filters
                        </td>
                      </tr>
                    ) : filteredPlans.map((plan) => {
                      const remaining = Math.max(0, (plan.booking_total || 0) - (plan.booking_amount_paid || 0));
                      const overdue = isOverdue(plan);
                      const dueSoon = isDueSoon(plan);
                      
                      return (
                        <tr 
                          key={plan.plan_id} 
                          className={cn(
                            "border-b border-slate-100 hover:bg-slate-50 cursor-pointer",
                            overdue && "bg-red-50"
                          )}
                          onClick={() => openPlanDrawer(plan)}
                        >
                          <td className="py-3 px-3">
                            <span className="font-mono text-xs text-slate-600">
                              {plan.booking_id?.slice(0, 15)}...
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="font-medium text-slate-900">{plan.customer_name}</div>
                            <div className="text-xs text-slate-500">{plan.customer_email}</div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <Badge className={cn("text-xs", statusColors[plan.status])}>
                                {statusLabels[plan.status] || plan.status}
                              </Badge>
                              {overdue && (
                                <Badge className="text-xs bg-red-100 text-red-700">Overdue</Badge>
                              )}
                              {dueSoon && !overdue && (
                                <Badge className="text-xs bg-orange-100 text-orange-700">Due Soon</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-slate-900">
                            {formatCurrency(plan.monthly_amount)}/mo
                          </td>
                          <td className="py-3 px-3">
                            {plan.next_payment_date ? (
                              <span className={overdue ? 'text-red-600 font-medium' : 'text-slate-700'}>
                                {formatDate(plan.next_payment_date)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-emerald-600">
                            {formatCurrency(plan.booking_amount_paid)}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <span className={cn(
                              "font-medium",
                              remaining > 0 ? "text-orange-600" : "text-slate-400"
                            )}>
                              {formatCurrency(remaining)}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            {plan.card_brand && plan.card_last4 ? (
                              <span className="text-xs text-slate-600">
                                {plan.card_brand} •••• {plan.card_last4}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPlanDrawer(plan);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredPlans.map((plan) => {
                  const remaining = Math.max(0, (plan.booking_total || 0) - (plan.booking_amount_paid || 0));
                  const overdue = isOverdue(plan);
                  
                  return (
                    <div 
                      key={plan.plan_id}
                      className={cn(
                        "border border-slate-200 rounded-lg p-4 hover:bg-slate-50 cursor-pointer",
                        overdue && "border-red-300 bg-red-50"
                      )}
                      onClick={() => openPlanDrawer(plan)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-900">{plan.customer_name}</p>
                          <p className="text-xs text-slate-500 font-mono">{plan.booking_id?.slice(0, 15)}...</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className={cn("text-xs", statusColors[plan.status])}>
                            {statusLabels[plan.status]}
                          </Badge>
                          {overdue && (
                            <Badge className="text-xs bg-red-100 text-red-700">Overdue</Badge>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500 text-xs">Instalment</p>
                          <p className="font-medium">{formatCurrency(plan.monthly_amount)}/mo</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Paid</p>
                          <p className="font-medium text-emerald-600">{formatCurrency(plan.booking_amount_paid)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Remaining</p>
                          <p className={cn(
                            "font-medium",
                            remaining > 0 ? "text-orange-600" : "text-slate-400"
                          )}>{formatCurrency(remaining)}</p>
                        </div>
                      </div>
                      {plan.next_payment_date && (
                        <p className={cn(
                          "text-xs mt-2",
                          overdue ? "text-red-600" : "text-slate-500"
                        )}>
                          Next: {formatDate(plan.next_payment_date)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Payment Plan Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedPlan && (
            <>
              <SheetHeader className="pb-4 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="text-lg font-semibold text-slate-900">
                      {selectedPlan.customer_name}
                    </SheetTitle>
                    <p className="text-sm text-slate-500 font-mono mt-1">
                      {selectedPlan.booking_id}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge className={cn("text-sm", statusColors[selectedPlan.status])}>
                      {statusLabels[selectedPlan.status]}
                    </Badge>
                    {isOverdue(selectedPlan) && (
                      <Badge className="text-sm bg-red-100 text-red-700">Overdue</Badge>
                    )}
                  </div>
                </div>
              </SheetHeader>

              <div className="py-4 space-y-4">
                {/* Financial Summary */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Booking Total</p>
                      <p className="font-semibold text-slate-900">{formatCurrency(selectedPlan.booking_total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Amount Paid</p>
                      <p className="font-semibold text-emerald-600">{formatCurrency(selectedPlan.booking_amount_paid)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Remaining Balance</p>
                      <p className="font-semibold text-orange-600">
                        {formatCurrency(Math.max(0, (selectedPlan.booking_total || 0) - (selectedPlan.booking_amount_paid || 0)))}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Instalment</p>
                      <p className="font-semibold text-slate-900">{formatCurrency(selectedPlan.monthly_amount)}/month</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>{selectedPlan.installments_made ?? selectedPlan.payments_made ?? 0} instalments made</span>
                      <span>{Math.round(((selectedPlan.booking_amount_paid || 0) / (selectedPlan.booking_total || 1)) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full">
                      <div 
                        className="h-full bg-violet-600 rounded-full" 
                        style={{ width: `${Math.min(100, ((selectedPlan.booking_amount_paid || 0) / (selectedPlan.booking_total || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <Section title="Payment Schedule" icon={Calendar}>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Next Payment</span>
                      <span className={isOverdue(selectedPlan) ? "text-red-600 font-medium" : "text-slate-900"}>
                        {selectedPlan.next_payment_date ? formatDate(selectedPlan.next_payment_date) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">End Date</span>
                      <span className="text-slate-900">{selectedPlan.end_date ? formatDate(selectedPlan.end_date) : 'N/A'}</span>
                    </div>
                    {selectedPlan.last_payment_date && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Last Payment</span>
                        <span className="text-slate-900">{formatDate(selectedPlan.last_payment_date)}</span>
                      </div>
                    )}
                  </div>
                </Section>

                {/* Payment Method */}
                <Section title="Payment Method" icon={CreditCard}>
                  {selectedPlan.card_brand && selectedPlan.card_last4 ? (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <CreditCard className="w-8 h-8 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">{selectedPlan.card_brand}</p>
                        <p className="text-sm text-slate-500">•••• •••• •••• {selectedPlan.card_last4}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No card on file</p>
                  )}
                </Section>

                {/* Payment History */}
                {selectedPlan.payment_history?.length > 0 && (
                  <Section title={`Payment History (${selectedPlan.payment_history.length})`} icon={Clock}>
                    <div className="space-y-2">
                      {selectedPlan.payment_history.map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{formatCurrency(payment.amount)}</p>
                            <p className="text-xs text-slate-500">{formatDate(payment.date || payment.created_at)}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {payment.status || 'Completed'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Last Error */}
                {selectedPlan.last_payment_error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800">Last Payment Error</p>
                    <p className="text-sm text-red-600 mt-1">{selectedPlan.last_payment_error}</p>
                  </div>
                )}

                {/* Actions */}
                {(selectedPlan.status === 'active' || selectedPlan.status === 'paused') && (
                  <div className="space-y-3 pt-4 border-t border-slate-200">
                    <p className="text-sm font-medium text-slate-700">Actions</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {selectedPlan.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setActionDialog({ open: true, type: 'process', plan: selectedPlan })}
                            disabled={processingPlanId === selectedPlan.plan_id}
                          >
                            {processingPlanId === selectedPlan.plan_id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2" />
                            )}
                            Process Payment
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePausePlan(selectedPlan)}
                          >
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </Button>
                        </>
                      )}
                      
                      {selectedPlan.status === 'paused' && (
                        <Button
                          size="sm"
                          onClick={() => handleResumePlan(selectedPlan)}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActionDialog({ open: true, type: 'cancel', plan: selectedPlan })}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel Plan
                      </Button>
                    </div>
                    
                    {/* View Booking Link */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => handleViewBooking(selectedPlan)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Booking
                    </Button>
                    
                    {/* Advanced Actions */}
                    <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                      <CollapsibleTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full justify-between text-slate-500"
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            Advanced
                          </span>
                          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-2 space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-purple-600"
                          onClick={() => handleRecalculateFromBooking(selectedPlan)}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Recalculate from Booking
                        </Button>
                        <p className="text-xs text-slate-500">
                          Syncs plan balance with booking payments. Use after manual payment adjustments.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Process Payment Confirmation Dialog */}
      <AlertDialog 
        open={actionDialog.open && actionDialog.type === 'process'} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, plan: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Payment</AlertDialogTitle>
            <AlertDialogDescription>
              This will charge <strong>{formatCurrency(actionDialog.plan?.monthly_amount)}</strong> to the stored card 
              ({actionDialog.plan?.card_brand} •••• {actionDialog.plan?.card_last4}).
              <br /><br />
              Customer: <strong>{actionDialog.plan?.customer_name}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleProcessPayment(actionDialog.plan)}
              disabled={processingPlanId === actionDialog.plan?.plan_id}
            >
              {processingPlanId === actionDialog.plan?.plan_id ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Charge Card'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Plan Confirmation Dialog */}
      <AlertDialog 
        open={actionDialog.open && actionDialog.type === 'cancel'} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, plan: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Payment Plan</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the payment plan for <strong>{actionDialog.plan?.customer_name}</strong>.
              <br /><br />
              <strong>Note:</strong> This only cancels the automatic payment plan. It does NOT cancel the actual Weekender booking.
              The customer will still owe any remaining balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Plan</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleCancelPlan(actionDialog.plan)}
              className="bg-red-600 hover:bg-red-700"
            >
              Cancel Plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
